/**
 * Which undo material each tool needs — the hard gate.
 *
 * Two ways to put the socket back on a device that never saw the apply:
 *
 *   RECIPE  re-run the tool from its serialized parameters on top of an earlier
 *           mesh. Costs nothing to store. Only valid when the parameters are
 *           small enough to survive the checkpoint metadata sanitiser *and* the
 *           tool has a verified deterministic replay handler.
 *   BEFORE  restore a stored copy of the socket from immediately before the
 *           apply. Always correct, costs one mesh blob.
 *
 * The table below is the *intent*. `canUseRecipe` is the gate that decides at
 * runtime, and it only ever downgrades RECIPE → BEFORE, never the reverse — a
 * missing handler or truncated parameters must never silently produce the wrong
 * geometry.
 *
 * Why these tools are RECIPE: their entire input is a handful of scalars or a
 * short array (a length in mm, a reduction percentage, a per-segment flare
 * table, a trimline curve). Everything else is either interactive (the user
 * painted or dragged it), ML-driven, or a CSG boolean whose parameters include a
 * per-vertex buffer — none of which reproduces reliably, and all of which get
 * their large arrays stripped by `sanitizeForMetadata` on the way to the server.
 *
 * A before-mesh is written for *every* geometry tool regardless, so the split
 * can be re-tuned later without a data migration: flipping a tool to RECIPE only
 * changes which source is preferred, not what exists.
 */
import { TOOLS_CONFIG, TOOL_CONFIG_BY_ID } from '../../../shared/constants/tools';
import { getOperationHandler } from '../operationLog/operationRegistry';

const LOG_PREFIX = '[UndoStrategy]';

export const UNDO_STRATEGY = {
  /** Restore from a stored pre-apply mesh. */
  BEFORE: 'before',
  /** Re-run the tool from its parameters. */
  RECIPE: 'recipe',
  /** Tool does not change geometry — nothing to undo. */
  NONE: 'none',
};

const T = TOOLS_CONFIG;

/**
 * Tools whose parameters fully describe their geometry change.
 * Keep this list short and only add a tool once its replay handler has been
 * verified against the live tool in-app.
 */
export const RECIPE_ELIGIBLE_TOOL_IDS = new Set([
  T.MAIN_PROCESS.ELONGATION.main_elongation_elongation.id,
  T.MAIN_PROCESS.TRIMLINE.main_trimline_trimline.id,
  T.MAIN_PROCESS.THICKNESS.main_thickness_thickness.id,
  T.MAIN_PROCESS.FLARES.main_flares_flare.id,
  T.MAIN_PROCESS.RECTIFICATION.main_rectification_global_reduction.id,
  T.MAIN_PROCESS.RECTIFICATION.main_rectification_measure_reduction.id,
]);

/**
 * Tools that measure or preview without committing geometry. They never produce
 * an undoable command, so they need no undo material; listed explicitly so a new
 * tool falling through to the BEFORE default is a deliberate choice, not an
 * oversight.
 */
export const NON_MUTATING_TOOL_IDS = new Set(
  Object.values(TOOL_CONFIG_BY_ID)
    .filter((cfg) => cfg?.category === 'measurement')
    .map((cfg) => cfg.id),
);

/**
 * Declared strategy for a tool, ignoring runtime availability.
 * Unknown tools default to BEFORE — the safe side.
 *
 * @param {string} toolId
 * @returns {'before' | 'recipe' | 'none'}
 */
export function strategyForTool(toolId) {
  if (!toolId) return UNDO_STRATEGY.NONE;
  if (NON_MUTATING_TOOL_IDS.has(toolId)) return UNDO_STRATEGY.NONE;
  if (RECIPE_ELIGIBLE_TOOL_IDS.has(toolId)) return UNDO_STRATEGY.RECIPE;
  return UNDO_STRATEGY.BEFORE;
}

/** True when this tool should get a stored pre-apply mesh. */
export function needsBeforeMesh(toolId) {
  return strategyForTool(toolId) !== UNDO_STRATEGY.NONE;
}

/**
 * Did `sanitizeForMetadata` strip anything out of these params on the way to the
 * server? Large arrays and typed arrays are replaced with an `_omitted` marker,
 * and a recipe missing its per-vertex buffer would replay to the wrong shape.
 *
 * @param {unknown} params
 * @returns {boolean}
 */
export function paramsWereTruncated(params) {
  if (params == null || typeof params !== 'object') return false;
  if (Array.isArray(params)) return params.some((v) => paramsWereTruncated(v));
  if (Object.prototype.hasOwnProperty.call(params, '_omitted')) return true;
  return Object.values(params).some((v) => paramsWereTruncated(v));
}

/**
 * The gate. Recipe undo is only permitted when all four hold:
 *
 *   1. the tool is declared recipe-eligible;
 *   2. a replay handler is registered for it;
 *   3. that handler is not flagged non-deterministic;
 *   4. the recorded parameters are intact (nothing stripped on upload).
 *
 * @param {string} toolId
 * @param {{ params?: Record<string, unknown> } | null} [operation]
 * @returns {{ ok: boolean, reason?: string }}
 */
export function canUseRecipe(toolId, operation) {
  if (!RECIPE_ELIGIBLE_TOOL_IDS.has(toolId)) {
    return { ok: false, reason: 'not_recipe_eligible' };
  }
  const handler = getOperationHandler(toolId);
  if (!handler) return { ok: false, reason: 'no_replay_handler' };
  if (handler.deterministic === false) return { ok: false, reason: 'non_deterministic' };
  if (!operation) return { ok: false, reason: 'no_operation_recorded' };
  if (paramsWereTruncated(operation.params)) {
    return { ok: false, reason: 'params_truncated' };
  }
  return { ok: true };
}

/**
 * Log-friendly summary of why a tool is taking the path it is. Used by the
 * resolver so a support ticket can be diagnosed from the console alone.
 *
 * @param {string} toolId
 * @param {{ params?: Record<string, unknown> } | null} [operation]
 */
export function describeStrategy(toolId, operation) {
  const declared = strategyForTool(toolId);
  if (declared !== UNDO_STRATEGY.RECIPE) return `${declared}`;
  const gate = canUseRecipe(toolId, operation);
  return gate.ok ? 'recipe' : `before (recipe rejected: ${gate.reason})`;
}

export { LOG_PREFIX as UNDO_STRATEGY_LOG_PREFIX };
